import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Tooltip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Store,
  AccountBalance,
  Person,
  Logout,
  Settings,
  Business,
  Assignment,
  LocalShipping,
  PendingActions,
  Add,
  List as ListIcon,
  Group,
  ReceiptLong,
  Inventory,
  ArrowBack,
  LightMode,
  DarkMode,
  Work,
  Assessment,
  History
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import useMediaQuery from '@mui/material/useMediaQuery';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

const drawerWidth = 280;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  // Helper to convert path segment to human-friendly label
  const pathNameMap = {
    dashboard: 'Dashboard',
    'admin': 'Admin',
    'users': 'User Management',
    'activity-logs': 'Activity Log',
    'karigar': 'Karigar',
    'orders': 'Order List',
    'add': 'Add',
    'edit': 'Edit',
    'direct-orders': 'Direct Order List',
    'direct-order': 'Direct Order Entry',
    'items': 'Items',
    'item-issue': 'Item Issue',
    'work-receipt': 'Work Receipt',
    'pending-issues': 'Pending Issues',
    'pending-list': 'Pending List',
    'metal-issue': 'Metal Issue',
    'overview': 'Overview',
    'shop': 'Shop',
    'customer': 'Customer',
    'customer-list': 'Customer List',
    'order-list': 'Order List',
    'order-form': 'Order Entry',
    'order-main': 'Order Management',
    'transaction': 'Transaction',
    '22k': '22K Gold',
    '24k': '24K Gold',
    'list': 'List',
    'form': 'Form',
    'add-item': 'Add Item',
    'despatch': 'Despatch',
    'karigar-list': 'Karigar List',
    'karigar-form': 'Karigar Form',
    'karigar-overview': 'Karigar Overview',
    'shop-overview': 'Shop Overview',
    'shop-karigar-list': 'Shop Karigar List',
    'shop-karigar-form': 'Shop Karigar Form',
    'shop-metal-issue': 'Shop Metal Issue',
    'shop-pending-issues': 'Shop Pending Issues',
    'shop-pending-list': 'Shop Pending List',
    'shop-work-receipt': 'Shop Work Receipt',
    'shop-direct-order': 'Shop Direct Order Entry',
    'shop-direct-order-list': 'Shop Direct Order List',
    'shop-item-issue': 'Shop Item Issue',
    'shop-despatch': 'Shop Despatch',
    'shop-order-list': 'Shop Order List',
    'shop-order-form': 'Shop Order Entry',
    'shop-customer-list': 'Shop Customer List',
    'shop-customer-form': 'Shop Customer Form',
    'shop-add-item': 'Shop Add Item',
    // Add more as needed
  };

  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    const crumbs = [
      <Link underline="hover" color="inherit" key="dashboard" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>
        Dashboard
      </Link>
    ];
    let path = '';
    pathnames.forEach((segment, idx) => {
      path += '/' + segment;
      const isLast = idx === pathnames.length - 1;
      const label = pathNameMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
      if (isLast) {
        crumbs.push(
          <Typography color="text.primary" key={path}>
            {label}
          </Typography>
        );
      } else {
        crumbs.push(
          <Link underline="hover" color="inherit" key={path} onClick={() => navigate(path)} sx={{ cursor: 'pointer' }}>
            {label}
          </Link>
        );
      }
    });
    return crumbs;
  };

  // Add a function to get the welcome message
  const getWelcomeMessage = () => {
    if (user?.role === 0) {
      return 'Welcome to TKM Group MIS Admin Dashboard';
    } else {
      const moduleName = user?.subadminType?.charAt(0).toUpperCase() + user?.subadminType?.slice(1);
      return `Welcome to TKM Group MIS - ${moduleName} Module`;
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getMenuItems = () => {
    const items = [];

    // Admin can see all modules
    if (user?.role === 0) {
      items.push(
        {
          text: 'Dashboard',
          icon: <Dashboard />,
          path: '/dashboard'
        },
        {
          text: 'User Management',
          icon: <People />,
          path: '/admin/users'
        },
        {
          text: 'Activity Log',
          icon: <History />,
          path: '/admin/activity-logs'
        },
        {
          text: 'Karigar',
          icon: <Business />,
          path: '/karigar',
          children: [
            { text: 'Order Entry', path: '/shop/orders/add', icon: <Add /> },
            { text: 'Order List', path: '/karigar/orders', icon: <ListIcon /> },
            { text: 'Direct Order List', path: '/karigar/direct-orders', icon: <ListIcon /> },
            { text: 'Direct Order Entry', path: '/karigar/direct-order', icon: <Assignment /> },
            { text: 'Update Order', path: '/update-order', icon: <Assignment /> },
            { text: 'Update Direct Order', path: '/update-direct-order', icon: <Assignment /> },
            { text: 'Add Item', path: '/shop/items/add', icon: <Add /> },
            { text: 'Despatch', path: '/karigar/despatch', icon: <LocalShipping /> },
            { text: 'Item Issue', path: '/karigar/item-issue', icon: <Assignment /> },
            { text: 'Work Receipt', path: '/karigar/work-receipt', icon: <ReceiptLong /> },
            { text: 'Pending Issues', path: '/karigar/pending-issues', icon: <PendingActions /> },
            { text: 'Pending List', path: '/karigar/pending-list', icon: <PendingActions /> },
            { text: 'Metal Issue', path: '/karigar/metal-issue', icon: <Inventory /> },
            { text: 'Karigar Overview', path: '/karigar/overview', icon: <Dashboard /> },
            { text: 'Karigar List', path: '/karigar', icon: <Dashboard /> }
          ]
        },
        {
          text: 'Shop',
          icon: <Business />,
          path: '/shop',
          children: [
            { text: 'Order Entry', path: '/shop/orders/add', icon: <Add /> },
            { text: 'Order List', path: '/shop/orders', icon: <ListIcon /> },
            { text: 'Direct Order List', path: '/shop/direct-orders', icon: <ListIcon /> },
            { text: 'Direct Order Entry', path: '/shop/direct-order', icon: <Assignment /> },
            { text: 'Add Item', path: '/shop/items/add', icon: <Add /> },
            { text: 'Despatch', path: '/shop/despatch', icon: <LocalShipping /> },
            { text: 'Item Issue', path: '/shop/item-issue', icon: <Assignment /> },
            { text: 'Work Receipt', path: '/shop/work-receipt', icon: <ReceiptLong /> },
            { text: 'Pending Issues', path: '/shop/pending-issues', icon: <PendingActions /> },
            { text: 'Pending List', path: '/shop/pending-list', icon: <PendingActions /> },
            { text: 'Metal Issue', path: '/shop/metal-issue', icon: <Inventory /> },
            { text: 'Karigar Overview', path: '/shop/overview', icon: <Dashboard /> }
          ]
        },
        {
          text: 'Transaction',
          icon: <AccountBalance />,
          path: '/transaction/22k',
          children: [
            { text: '22K Gold', path: '/transaction/22k', icon: <AccountBalance /> },
            { text: '24K Gold', path: '/transaction/24k', icon: <AccountBalance /> },
            { text: 'Other Transaction 1', path: '/transaction/other1', icon: <Work /> },
            { text: 'Other Transaction 2', path: '/transaction/other2', icon: <Assessment /> },
            { text: 'Total Balance', path: '/transaction/total-balance', icon: <Store /> },
            { text: 'Previous Balance', path: '/transaction/previous-balance', icon: <Inventory /> }
          ]
        }
      );
    } else {
      // Subadmin sees only their module
      switch (user?.subadminType) {
        case 'karigar':
          items.push(
            {
              text: 'Dashboard',
              icon: <Dashboard />,
              path: '/dashboard'
            },
            {
              text: 'Karigar',
              icon: <Business />,
              path: '/karigar',
              children: [
                { text: 'Order Entry', path: '/shop/orders/add', icon: <Add /> },
                { text: 'Order List', path: '/karigar/orders', icon: <ListIcon /> },
                { text: 'Direct Order List', path: '/karigar/direct-orders', icon: <ListIcon /> },
                { text: 'Direct Order Entry', path: '/karigar/direct-order', icon: <Assignment /> },
                { text: 'Add Item', path: '/shop/items/add', icon: <Add /> },
                { text: 'Despatch', path: '/karigar/despatch', icon: <LocalShipping /> },
                { text: 'Item Issue', path: '/karigar/item-issue', icon: <Assignment /> },
                { text: 'Work Receipt', path: '/karigar/work-receipt', icon: <ReceiptLong /> },
                { text: 'Pending Issues', path: '/karigar/pending-issues', icon: <PendingActions /> },
                { text: 'Pending List', path: '/karigar/pending-list', icon: <PendingActions /> },
                { text: 'Metal Issue', path: '/karigar/metal-issue', icon: <Inventory /> },
                { text: 'Karigar List', path: '/karigar', icon: <Dashboard /> },
                { text: 'Karigar Overview', path: '/karigar/overview', icon: <Dashboard /> }
              ]
            }
          );
          break;
        case 'shop':
          items.push(
            {
              text: 'Dashboard',
              icon: <Dashboard />,
              path: '/dashboard'
            },
            {
              text: 'Shop',
              icon: <Store />,
              path: '/shop',
              children: [
                { text: 'Order Entry', path: '/shop/orders/add', icon: <Add /> },
                { text: 'Order List', path: '/shop/orders', icon: <ListIcon /> },
                { text: 'Direct Order List', path: '/shop/direct-orders', icon: <ListIcon /> },
                { text: 'Direct Order Entry', path: '/shop/direct-order', icon: <Assignment /> },
                { text: 'Add Item', path: '/shop/items/add', icon: <Add /> },
                { text: 'Despatch', path: '/shop/despatch', icon: <LocalShipping /> },
                { text: 'Item Issue', path: '/shop/item-issue', icon: <Assignment /> },
                { text: 'Work Receipt', path: '/shop/work-receipt', icon: <ReceiptLong /> },
                { text: 'Pending Issues', path: '/shop/pending-issues', icon: <PendingActions /> },
                { text: 'Pending List', path: '/shop/pending-list', icon: <PendingActions /> },
                { text: 'Metal Issue', path: '/shop/metal-issue', icon: <Inventory /> },
                { text: 'Karigar Overview', path: '/shop/overview', icon: <Dashboard /> }
              ]
            }
          );
          break;
        case 'transaction':
          items.push(
            {
              text: 'Dashboard',
              icon: <Dashboard />,
              path: '/dashboard'
            },
            {
              text: 'Transaction',
              icon: <AccountBalance />,
              path: '/transaction/22k',
              children: [
                { text: '22K Gold', path: '/transaction/22k', icon: <AccountBalance /> },
                { text: '24K Gold', path: '/transaction/24k', icon: <AccountBalance /> },
                { text: 'Other Transaction 1', path: '/transaction/other1', icon: <Work /> },
                { text: 'Other Transaction 2', path: '/transaction/other2', icon: <Assessment /> },
                { text: 'Total Balance', path: '/transaction/total-balance', icon: <Store /> },
                { text: 'Previous Balance', path: '/transaction/previous-balance', icon: <Inventory /> }
              ]
            }
          );
          break;
        default:
          items.push({
            text: 'Dashboard',
            icon: <Dashboard />,
            path: '/dashboard'
          });
      }
    }

    return items;
  };

  const renderMenuItem = (item, level = 0) => {
    const isActive = location.pathname === item.path;
    const hasChildren = item.children && item.children.length > 0;

    return (
      <Box key={item.text}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              if (!hasChildren) {
                navigate(item.path);
                setMobileOpen(false);
              }
            }}
            sx={{
              pl: level * 2 + 2,
              backgroundColor: isActive ? 'primary.light' : 'transparent',
              '&:hover': {
                backgroundColor: isActive ? 'primary.light' : 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              sx={{ 
                color: isActive ? 'primary.main' : 'inherit',
                fontWeight: isActive ? 600 : 400
              }}
            />
          </ListItemButton>
        </ListItem>
        
        {hasChildren && (
          <List component="div" disablePadding>
            {item.children.map((child) => renderMenuItem(child, level + 1))}
          </List>
        )}
      </Box>
    );
  };

  const drawer = (
    <Box>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
          TKM Group MIS
        </Typography>
        <Chip 
          label={user?.role === 0 ? 'Admin' : `${user?.subadminType?.charAt(0).toUpperCase() + user?.subadminType?.slice(1)} Subadmin`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ mt: 1 }}
        />
      </Box>
      <Divider />
      <List>
        {getMenuItems().map((item) => renderMenuItem(item))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ width: '100%' }}>
        <Toolbar sx={{ minHeight: 80, py: 1, mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 0 }}>
              <Typography variant="h6" noWrap component="div" sx={{}}>
                {location.pathname === '/dashboard' ? 'Dashboard' : 
                  location.pathname.split('/').pop().charAt(0).toUpperCase() + 
                  location.pathname.split('/').pop().slice(1).replace(/([A-Z])/g, ' $1')}
              </Typography>
              <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem', ml: 2, mt: 0.5 }}>{getBreadcrumbs()}</Breadcrumbs>
            </Box>
            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'inherit' }}>
                {getWelcomeMessage()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={`Switch to ${mode === 'light' ? 'Dark' : 'Light'} Mode`}>
                <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 1 }}>
                  {mode === 'light' ? <DarkMode /> : <LightMode />}
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleProfileMenuOpen} color="inherit">
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {user?.name?.[0]?.toUpperCase() || user?.username?.[0]?.toUpperCase() || '?'}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/dashboard'); }}>Dashboard</MenuItem>
                <MenuItem onClick={() => { handleProfileMenuClose(); handleLogout(); }}>Logout</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
      >
        {drawer}
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: '100vw', mt: '70px' }}>
        {/* Breadcrumbs removed from here */}
        {children}
      </Box>
    </Box>
  );
};

export default Layout;